/* eslint-disable max-lines */
import {
  useCreatePresenceZone,
  useDeletePresenceZone,
  usePresenceZones,
  useUpdatePresenceZone,
} from '@hakimo-ui/hakimo/data-access';
import {
  CameraDetail,
  PresenceRule,
  PresenceZone,
  PresenceZoneKind,
} from '@hakimo-ui/hakimo/types';
import {
  CanvasDimensions,
  createDefaultLine,
  createDefaultZone,
  ImageResolution,
  rescaleZones,
  reverseLine,
  SaveIcon,
  SpecialZoneType,
  ZoneCanvas,
  ZonePolygonData,
} from '@hakimo-ui/hakimo/ui-elements';
import { toast, useCanUpdatePresenceZone } from '@hakimo-ui/hakimo/util';
import { Alert, Button, InputField } from '@hakimo-ui/shared/ui-base';
import {
  ArrowPathIcon,
  ArrowsRightLeftIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DeleteZoneModal } from '../special-zones/DeleteZoneModal';
import {
  canvasZoneToRequest,
  kindFor,
  presenceZonesToCanvas,
  reversesLine,
  samePoints,
} from './presenceZoneConvert';
import { ruleTargetsZone } from './ruleSentence';

interface Props {
  camera: CameraDetail;
  rules: PresenceRule[];
}

const NOUN: Record<PresenceZoneKind, string> = { area: 'Zone', line: 'Line' };

// The API notes a line drawn too close to the frame edge, where a person
// stepping out cannot be told from one walking into view; pass it on.
function toastSaved(zone: PresenceZone, verb: string) {
  toast(`${NOUN[zone.kind]} "${zone.name}" ${verb}`, { type: 'success' });
  (zone.warnings ?? []).forEach((w) => toast(w, { type: 'warning' }));
}

/**
 * Named presence zones and lines on the camera image.
 *
 * Reuses the Konva canvas the special-zones editor draws with; what differs
 * is the data. A special zone is an anonymous polygon on the camera row and
 * is saved as a whole list. A presence zone has its own id and name and is
 * saved one at a time, because rules target it by name and history points
 * at it by id. A line is the same row with a different kind: an open
 * polyline whose point order sets the crossing direction, shown as an
 * arrow and flipped by reversing the points.
 */
export function PresenceZonesEditor({ camera, rules }: Props) {
  const canUpdate = useCanUpdatePresenceZone();
  const {
    data: serverZones,
    isLoading,
    isFetching,
    isError,
    error,
  } = usePresenceZones(camera.id);

  const [canvasDimensions, setCanvasDimensions] =
    useState<CanvasDimensions | null>(null);
  const [imageResolution, setImageResolution] =
    useState<ImageResolution | null>(null);
  const [zones, setZones] = useState<ZonePolygonData[]>([]);
  const [names, setNames] = useState<Record<string, string>>({});
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [newIds, setNewIds] = useState<Set<string>>(new Set());
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const initialRef = useRef<ZonePolygonData[]>([]);
  const prevDimensionsRef = useRef<CanvasDimensions | null>(null);

  // Rebuild from the server whenever it changes and nothing is mid-edit.
  //
  // A save clears the selection before its refetch lands, so without the
  // isFetching guard the rebuild runs against the list the save just made
  // stale: a new zone disappears and an edited polygon snaps back until the
  // refetch arrives. Waiting keeps what was drawn on screen throughout.
  useEffect(() => {
    if (!serverZones || !canvasDimensions || !imageResolution) return;
    if (selectedId || isFetching) return;
    const canvasZones = presenceZonesToCanvas(
      serverZones,
      imageResolution,
      canvasDimensions
    );
    setZones(canvasZones);
    setNames(Object.fromEntries(serverZones.map((z) => [z.id, z.name])));
    initialRef.current = canvasZones;
    prevDimensionsRef.current = canvasDimensions;
  }, [serverZones, canvasDimensions, imageResolution, selectedId, isFetching]);

  // Rescale when the canvas resizes.
  useEffect(() => {
    const prev = prevDimensionsRef.current;
    if (!canvasDimensions || !prev) return;
    if (
      prev.width === canvasDimensions.width &&
      prev.height === canvasDimensions.height
    )
      return;
    setZones((current) => rescaleZones(current, prev, canvasDimensions));
    initialRef.current = rescaleZones(
      initialRef.current,
      prev,
      canvasDimensions
    );
    prevDimensionsRef.current = canvasDimensions;
  }, [canvasDimensions]);

  const createMutation = useCreatePresenceZone(camera.id, (zone) => {
    toastSaved(zone, 'created');
    setSelectedId(null);
  });
  const updateMutation = useUpdatePresenceZone(
    camera.id,
    selectedId ?? '',
    (zone) => {
      toastSaved(zone, 'saved');
      setSelectedId(null);
    }
  );
  const deleteMutation = useDeletePresenceZone(
    camera.id,
    selectedId ?? '',
    () => {
      setIsDeleteOpen(false);
      setSelectedId(null);
    }
  );

  // Selecting another zone (or finishing a save) drops the previous zone's
  // error; otherwise it reads as a problem with whatever is now open.
  useEffect(() => {
    createMutation.reset();
    updateMutation.reset();
    deleteMutation.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const selected = useMemo(
    () => zones.find((z) => z.id === selectedId) ?? null,
    [zones, selectedId]
  );
  const isNew = selectedId != null && newIds.has(selectedId);
  const selectedName = selectedId ? names[selectedId] ?? '' : '';
  const initial = useMemo(
    () => initialRef.current.find((z) => z.id === selectedId) ?? null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedId, zones]
  );
  const isDirty =
    !!selected &&
    (isNew ||
      !initial ||
      !samePoints(selected, initial) ||
      selectedName.trim() !== (initial.label ?? ''));

  // Rules that count one direction across this line. A saved line turned
  // round makes each of them count the other way.
  const reversedRules =
    selected &&
    initial &&
    kindFor(selected) === 'line' &&
    reversesLine(initial, selected)
      ? rules.filter(
          (r) =>
            r.type === 'line_crossing' &&
            r.direction !== 'any' &&
            ruleTargetsZone(r, { id: selected.id, name: initial.label ?? '' })
        ).length
      : 0;

  const discard = useCallback(
    (zoneId: string) => {
      if (newIds.has(zoneId)) {
        setZones((prev) => prev.filter((z) => z.id !== zoneId));
        setNames((prev) => {
          const next = { ...prev };
          delete next[zoneId];
          return next;
        });
        setNewIds((prev) => {
          const next = new Set(prev);
          next.delete(zoneId);
          return next;
        });
        return;
      }
      const original = initialRef.current.find((z) => z.id === zoneId);
      if (original) {
        setZones((prev) => prev.map((z) => (z.id === zoneId ? original : z)));
        setNames((prev) => ({ ...prev, [zoneId]: original.label ?? '' }));
      }
    },
    [newIds]
  );

  const handleSelect = useCallback(
    (zoneId: string | null) => {
      if (selectedId && selectedId !== zoneId) discard(selectedId);
      setSelectedId(zoneId);
    },
    [selectedId, discard]
  );

  const handleZoneChange = useCallback((updated: ZonePolygonData) => {
    setZones((prev) =>
      prev.map((z) =>
        z.id === updated.id ? { ...updated, label: z.label } : z
      )
    );
  }, []);

  const handleAdd = useCallback(
    (kind: PresenceZoneKind) => {
      if (!canvasDimensions) return;
      if (selectedId) discard(selectedId);
      const zone = {
        ...(kind === 'line'
          ? createDefaultLine(canvasDimensions)
          : createDefaultZone(canvasDimensions, SpecialZoneType.PresenceZone)),
        label: '',
      };
      setZones((prev) => [...prev, zone]);
      setNames((prev) => ({ ...prev, [zone.id]: '' }));
      setNewIds((prev) => new Set(prev).add(zone.id));
      setSelectedId(zone.id);
    },
    [canvasDimensions, selectedId, discard]
  );

  // The same line the other way round: the arrow, and with it which
  // crossings read as "ltr", swaps sides. A geometry edit like any other.
  const handleFlip = useCallback(() => {
    if (!selectedId) return;
    setZones((prev) =>
      prev.map((z) =>
        z.id === selectedId ? { ...z, points: reverseLine(z.points) } : z
      )
    );
  }, [selectedId]);

  const handleRename = useCallback(
    (value: string) => {
      if (!selectedId) return;
      setNames((prev) => ({ ...prev, [selectedId]: value }));
      setZones((prev) =>
        prev.map((z) => (z.id === selectedId ? { ...z, label: value } : z))
      );
    },
    [selectedId]
  );

  const handleSave = useCallback(() => {
    if (!selected || !canvasDimensions || !imageResolution) return;
    const request = canvasZoneToRequest(
      selected,
      selectedName,
      canvasDimensions,
      imageResolution,
      { isNew }
    );
    if (isNew) {
      createMutation.mutate(request, {
        onSuccess: () => {
          setNewIds((prev) => {
            const next = new Set(prev);
            next.delete(selected.id);
            return next;
          });
        },
      });
    } else {
      updateMutation.mutate(request);
    }
  }, [
    selected,
    selectedName,
    canvasDimensions,
    imageResolution,
    isNew,
    createMutation,
    updateMutation,
  ]);

  const handleReset = useCallback(() => {
    if (!selectedId) return;
    discard(selectedId);
    if (newIds.has(selectedId)) setSelectedId(null);
  }, [selectedId, discard, newIds]);

  const confirmDelete = useCallback(() => {
    if (!selectedId) return;
    if (newIds.has(selectedId)) {
      discard(selectedId);
      setSelectedId(null);
      setIsDeleteOpen(false);
      return;
    }
    const deleted = selected ? NOUN[kindFor(selected)] : NOUN.area;
    deleteMutation.mutate(null, {
      onSuccess: () => toast(`${deleted} deleted`, { type: 'success' }),
    });
  }, [selectedId, selected, newIds, discard, deleteMutation]);

  const selectedIsLine = !!selected && kindFor(selected) === 'line';
  const Noun = NOUN[selected ? kindFor(selected) : 'area'];
  const nameProblem = selected
    ? selectedName.trim() === ''
      ? `Give the ${Noun.toLowerCase()} a name.`
      : serverZones?.some(
          (z) =>
            z.id !== selected.id &&
            z.name.toLowerCase() === selectedName.trim().toLowerCase()
        )
      ? 'Another zone already has this name.'
      : null
    : null;

  const isSaving = createMutation.isLoading || updateMutation.isLoading;
  // The delete error is shown inside its own modal, not here behind it.
  const mutationError = createMutation.error ?? updateMutation.error;

  const named = (serverZones ?? []).filter((z) => !z.isDefault);
  const areaCount = named.filter((z) => z.kind !== 'line').length;
  const lineCount = named.length - areaCount;
  const pending = zones.filter((z) => newIds.has(z.id));
  const countText = [
    `${areaCount} zone${areaCount === 1 ? '' : 's'}`,
    lineCount ? `${lineCount} line${lineCount === 1 ? '' : 's'}` : null,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <div className="bg-onlight-bg-1 dark:bg-ondark-bg-1 rounded-lg p-1">
      {isError && (
        <div className="p-3">
          <Alert type="error">{error?.message ?? 'Could not load zones'}</Alert>
        </div>
      )}
      <div className="flex flex-col gap-4 lg:flex-row">
        <div className="min-w-0 flex-1 overflow-hidden">
          <ZoneCanvas
            imageUrl={camera.imageUrl}
            zones={zones}
            selectedZoneId={selectedId}
            onZoneChange={handleZoneChange}
            onSelectZone={handleSelect}
            onCanvasDimensionsChange={setCanvasDimensions}
            onImageResolutionChange={setImageResolution}
            canUpdatePresenceZone={canUpdate}
          />
          <div className="flex items-center justify-between px-4 py-3">
            <div className="text-onlight-text-3 dark:text-ondark-text-3 text-sm">
              {isLoading ? 'Loading zones…' : countText}
            </div>
            {canUpdate && (
              <div className="flex gap-2">
                <Button
                  variant="icon"
                  onClick={() => handleAdd('area')}
                  title="Add zone"
                  className="bg-primary-500 hover:!bg-primary-600 flex gap-1 !text-white"
                >
                  <PlusIcon className="h-5 w-5" />
                  <span className="pr-1 text-sm">Zone</span>
                </Button>
                <Button
                  variant="icon"
                  onClick={() => handleAdd('line')}
                  title="Add line"
                  className="flex gap-1 bg-sky-600 !text-white hover:!bg-sky-700"
                >
                  <PlusIcon className="h-5 w-5" />
                  <span className="pr-1 text-sm">Line</span>
                </Button>
              </div>
            )}
          </div>
          {selectedIsLine && canUpdate && (
            <p className="text-onlight-text-3 dark:text-ondark-text-3 px-4 pb-3 text-xs">
              The arrow shows the direction a rule calls "in the arrow's
              direction". Flip it if crossings read the wrong way round. Drag a
              handle between two points to bend the line.
            </p>
          )}
        </div>

        <div className="flex w-full flex-col gap-2 lg:w-72">
          {!isLoading && named.length === 0 && pending.length === 0 && (
            <p className="text-onlight-text-3 dark:text-ondark-text-3 px-1 text-sm">
              No zones yet. Rules can still target the whole camera view; draw a
              zone to count a particular area, or a line to watch crossings.
            </p>
          )}
          {named.map((zone) => (
            <ZoneRow
              key={zone.id}
              label={zone.name}
              hint={zone.kind === 'line' ? 'Line' : undefined}
              selected={zone.id === selectedId}
              onClick={() => handleSelect(zone.id)}
            />
          ))}
          {pending.map((zone) => (
            <ZoneRow
              key={zone.id}
              label={
                names[zone.id] || `New ${NOUN[kindFor(zone)].toLowerCase()}`
              }
              hint={
                kindFor(zone) === 'line'
                  ? 'Line · not saved yet'
                  : 'Not saved yet'
              }
              selected={zone.id === selectedId}
              onClick={() => handleSelect(zone.id)}
            />
          ))}

          {selected && canUpdate && (
            <div className="border-onlight-line-2 dark:border-ondark-line-2 mt-2 space-y-3 border-t pt-3">
              <InputField
                type="text"
                label={`${Noun} name`}
                value={selectedName}
                onChange={(e) => handleRename(e.target.value)}
                error={!!nameProblem}
                errorText={nameProblem ?? undefined}
                autoFocus={isNew}
              />
              {reversedRules > 0 && (
                <Alert type="warning" size="small">
                  Saving turns {reversedRules} directional rule
                  {reversedRules === 1 ? '' : 's'} on this line around: what
                  counted as in the arrow's direction will count as against it.
                </Alert>
              )}
              {mutationError && (
                <Alert type="error" size="small">
                  {mutationError.message}
                </Alert>
              )}
              <div className="flex items-center justify-end gap-1">
                {selectedIsLine && (
                  <Button
                    variant="icon"
                    onClick={handleFlip}
                    title="Flip direction"
                  >
                    <ArrowsRightLeftIcon className="text-onlight-text-2 dark:text-ondark-text-2 h-5 w-5" />
                  </Button>
                )}
                <Button
                  variant="icon"
                  onClick={handleReset}
                  title="Reset"
                  disabled={!isDirty}
                >
                  <ArrowPathIcon className="text-onlight-text-2 dark:text-ondark-text-2 h-5 w-5" />
                </Button>
                <Button
                  variant="icon"
                  onClick={handleSave}
                  title={`Save ${Noun.toLowerCase()}`}
                  loading={isSaving}
                  disabled={!isDirty || !!nameProblem || isSaving}
                >
                  <SaveIcon className="text-onlight-text-2 dark:text-ondark-text-2 h-5 w-5" />
                </Button>
                <Button
                  variant="icon"
                  onClick={() => {
                    deleteMutation.reset();
                    setIsDeleteOpen(true);
                  }}
                  title={`Delete ${Noun.toLowerCase()}`}
                >
                  <TrashIcon className="text-onlight-text-2 dark:text-ondark-text-2 h-5 w-5" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
      <DeleteZoneModal
        open={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onConfirm={confirmDelete}
        isDeleting={deleteMutation.isLoading}
        error={deleteMutation.error?.message}
        noun={Noun.toLowerCase()}
      />
    </div>
  );
}

interface ZoneRowProps {
  label: string;
  hint?: string;
  selected: boolean;
  onClick?: () => void;
}

function ZoneRow({ label, hint, selected, onClick }: ZoneRowProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      className={clsx(
        'flex w-full items-center justify-between rounded-md border px-3 py-2 text-left',
        selected
          ? 'border-teal-600 bg-teal-50 dark:bg-teal-900/30'
          : 'border-onlight-line-2 dark:border-ondark-line-2',
        !onClick && 'cursor-default'
      )}
    >
      <span className="min-w-0">
        <span className="text-onlight-text-1 dark:text-ondark-text-1 block truncate text-sm">
          {label}
        </span>
        {hint && (
          <span className="text-onlight-text-3 dark:text-ondark-text-3 block text-xs">
            {hint}
          </span>
        )}
      </span>
    </button>
  );
}

export default PresenceZonesEditor;
